import { useState, useEffect } from "react";
import {
  isSignInWithEmailLink,
  signInWithEmailLink,
  updatePassword,
} from "firebase/auth";
import { auth } from "@/firebase";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORED_EMAIL_KEY = "hafani_invite_email";

export const AdminSetupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("check"); // "check" | "email" | "password" | "done" | "invalid"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const saved = window.localStorage.getItem(STORED_EMAIL_KEY);
      if (saved) {
        setEmail(saved);
        setStep("password");
      } else {
        setStep("email");
      }
    } else {
      setStep("invalid");
    }
  }, []);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    window.localStorage.setItem(STORED_EMAIL_KEY, email);
    setStep("password");
  };

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }
    if (password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    setLoading(true);
    try {
      const userCredential = await signInWithEmailLink(auth, email, window.location.href);
      window.localStorage.removeItem(STORED_EMAIL_KEY);

      await updatePassword(userCredential.user, password);

      const idToken = await userCredential.user.getIdToken();
      const res = await axios.post(`${API}/auth/firebase/admin-login`, { id_token: idToken });
      localStorage.setItem("hafani_admin_token", res.data.access_token);
      localStorage.setItem("hafani_admin_role", res.data.role);

      setStep("done");
      setTimeout(() => {
        if (res.data.role === "super_admin") {
          navigate("/admin/super");
        } else {
          navigate("/app/dashboard");
        }
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(detail || "Erreur lors de la configuration du compte.");
    } finally {
      setLoading(false);
    }
  };

  if (step === "check") {
    return <SetupShell><p className="text-slate-500 text-sm">Vérification du lien…</p></SetupShell>;
  }

  if (step === "invalid") {
    return (
      <SetupShell>
        <div className="text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-slate-700 text-sm">Ce lien est invalide ou a expiré.</p>
          <Link to="/admin/login" className="text-emerald-600 text-sm hover:underline">
            Retour à la connexion
          </Link>
        </div>
      </SetupShell>
    );
  }

  if (step === "done") {
    return (
      <SetupShell>
        <div className="text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-slate-700 text-sm font-medium">Compte configuré avec succès !</p>
          <p className="text-slate-400 text-xs">Redirection en cours…</p>
        </div>
      </SetupShell>
    );
  }

  if (step === "email") {
    return (
      <SetupShell title="Confirmez votre email" subtitle="Entrez l'adresse email sur laquelle vous avez reçu l'invitation.">
        <form onSubmit={handleEmailSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <label className="block text-xs font-medium tracking-wide text-slate-500 uppercase">
              Adresse email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="votre@email.com"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
            />
          </div>
          <button
            type="submit"
            className="w-full py-3 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:scale-[0.98] transition-all"
          >
            Continuer
          </button>
        </form>
      </SetupShell>
    );
  }

  return (
    <SetupShell title="Définissez votre mot de passe" subtitle="Choisissez un mot de passe sécurisé pour accéder à votre espace.">
      <form onSubmit={handleSetupSubmit} className="space-y-5">
        <div className="space-y-1.5">
          <label className="block text-xs font-medium tracking-wide text-slate-500 uppercase">
            Mot de passe
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="Minimum 8 caractères"
            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
          />
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-medium tracking-wide text-slate-500 uppercase">
            Confirmation
          </label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            placeholder="Répétez le mot de passe"
            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Configuration…" : "Activer mon compte"}
        </button>
      </form>
    </SetupShell>
  );
};

const SetupShell = ({ children, title, subtitle }) => (
  <div
    className="min-h-screen flex flex-col items-center justify-center px-4"
    style={{ backgroundColor: "#EAE8E4", fontFamily: "'DM Sans', sans-serif" }}
  >
    <div className="w-full max-w-md">
      <div className="text-center mb-10">
        <p className="text-xs font-medium tracking-[0.2em] text-slate-500 uppercase mb-2">Hafani MS</p>
        <h1
          className="text-4xl text-slate-900"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
        >
          {title || "Activation du compte"}
        </h1>
        {subtitle && <p className="text-slate-500 text-sm mt-3">{subtitle}</p>}
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 px-8 py-8">
        {children}
      </div>
    </div>
  </div>
);
